"use client";
import { getReportsUrl } from "@areahub/config";
import { useUpdateEffect } from "@chakra-ui/react";
import { loadingCtx } from "@ui/loading/src/loading-context";
import {
  startTransition,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAtom } from "jotai";
import scrollIntoView from "scroll-into-view-if-needed";
import { RedirectModalContext } from "../contexts/redirect-modal";
import { fetchAutocompleteResults } from "../utils/fetch-autocomplete-results";
import { fetchTopCities } from "../utils/fetch-top-cities";
import { type RscRouterParameters } from "./rsc-router-params";
import { usePlaceSearchModal } from "./use-place-search-modal";
import { useSearchResultQueryParams } from "./use-query-params";

export type SelectionMethod = "mouse" | "keyboard" | "none";

export function usePlaceSearch({ router, rsc }: RscRouterParameters) {
  // Hooks
  const modal = usePlaceSearchModal({ router, rsc });
  const [query, setQuery] = useState<string>("");
  const [results, setResults] = useState<any[]>([]);
  const [active, setActive] = useState<number>(0);
  const [ctx, setLoadingCtx] = useAtom(loadingCtx);
  const redirectModalContext = useContext(RedirectModalContext);
  const { updateSearchResult } = useSearchResultQueryParams();
  const [selectionMethod, setSelectionMethod] =
    useState<SelectionMethod>("none");
  const resultMenuRef = useRef<HTMLDivElement>(null);
  const resultRefs = useRef<Array<HTMLDivElement>>(new Array(results.length));

  // Variables
  const {
    onClose: onRedirectModalClose,
    onOpen: onRedirectModalOpen,
    saveCallback: saveRedirectModalCallback,
  } = redirectModalContext;

  // Reset the search places modal (clear input + results)
  const resetModal = () => {
    setQuery("");
    setResults([]);
    setSelectionMethod("none");
  };

  // The mouse was just used to hover over a result
  const onMouseSetActive = (active: number) => {
    setActive(active);
    setSelectionMethod("mouse");
  };

  // The keyboard was just used to hover over a result
  const onKeyboardSetActive = (active: number) => {
    setActive(active);
    setSelectionMethod("keyboard");
  };

  const redirectUser = (reportPath: string, label: string) => {
    //  Set loading screen context
    !rsc &&
      setLoadingCtx({
        ...ctx,
        props: {
          ...ctx.props,
          message: [`Generating your report for ${label}`],
        },
        isOpen: true,
      });
    router.push(
      `${
        process.env.APP_NAME !== "REPORTS" ? getReportsUrl() : ""
        /**
         * @description Redirects the user to the report page with the searched context.
         * The `searchedContext` is used to display the nearest city found near the searched context on the [`ReportHero`](https://gitlab.com/ambient-ridge-llc/areahub/clients/forequarters/-/blob/aebaaa52e25b36cfa62170f8d6a7e14af6d1fe72/apps/reports/components/report-hero/report-hero.tsx#L15-84) component's subtitle prop.
         */
      }/reports/city/${reportPath}?searchedContext=${label}`,
    );
  };

  // Redirects user to the report and closes the modal
  const continueToRedirect = (props: any) => {
    const { reportPath, addressLabel } = props;
    redirectUser(reportPath, addressLabel);
    onRedirectModalClose();
    resetModal();
  };

  const redirectResult = async () => {
    const closestTopCity = await fetchTopCities(results?.[active]?.title);
    const reportPath = closestTopCity?.path;
    if (reportPath) {
      modal.onClose();
      const address = results?.[active]?.address;
      // Save the callback function with its props so it can be run later
      saveRedirectModalCallback(continueToRedirect, {
        reportPath,
        addressLabel: address?.label,
      });
      /**
       * @description Updates the search result query parameter with the address label.
       * This is used to display the searched location on the redirect modal
       */
      updateSearchResult(address?.label);
      // Open the redirect modal
      onRedirectModalOpen();
    }
  };

  // Fetch new results when the query or modal are updated
  useEffect(() => {
    const fetchNewResults = async () => {
      setResults(await fetchAutocompleteResults(query));
    };
    if (!modal.isOpen) {
      resetModal();
    } else {
      startTransition(() => {
        fetchNewResults();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, modal.isOpen]);

  // When using the keyboard arrow keys to browse results, scroll to the active result if it's off screen
  useUpdateEffect(() => {
    if (results.length == 0 || selectionMethod !== "keyboard") return;
    const activeRef = resultRefs.current[active];
    if (!activeRef) return;
    scrollIntoView(activeRef, {
      scrollMode: "if-needed",
      block: "nearest",
      inline: "nearest",
      boundary: resultMenuRef.current,
    });
  }, [active]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          if (active + 1 < results.length) {
            onKeyboardSetActive(active + 1);
          }
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          if (active - 1 >= 0) {
            onKeyboardSetActive(active - 1);
          }
          break;
        }
        case "Enter": {
          if (results?.[active]) {
            redirectResult();
          }
          break;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [active, results, router],
  );

  return {
    modal,
    onKeyDown,
    results,
    active,
    onMouseSetActive,
    query,
    setQuery,
    redirectResult,
    resultMenuRef,
    resultRefs,
    router,
    rsc,
  };
}
